import { useEffect, useCallback, useRef } from "react";
import socketService from "../services/socketService";

interface UseWebSocketOptions {
  token?: string;
  userType?: string;
  restaurantId?: string;
  onOrderUpdated?: (data: any) => void;
  onOrderReceived?: (data: any) => void;
  onTableUpdated?: (data: any) => void;
  onFoodItemUpdated?: (data: any) => void;
  onDashboardUpdated?: (data: any) => void;
  onAuthenticated?: (data: any) => void;
  onAuthError?: (data: any) => void;
  onError?: (data: any) => void;
  onJoinedRestaurant?: (data: any) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const {
    token,
    userType,
    restaurantId,
    onOrderUpdated,
    onOrderReceived,
    onTableUpdated,
    onFoodItemUpdated,
    onDashboardUpdated,
    onAuthenticated,
    onAuthError,
    onError,
    onJoinedRestaurant,
  } = options;

  const isConnectedRef = useRef(false);
  const authenticatedRef = useRef(false);

  // Connect to WebSocket
  const connect = useCallback(() => {
    try {
      socketService.connect();
      isConnectedRef.current = true;
    } catch (error) {
      console.error("Failed to connect to WebSocket:", error);
    }
  }, []);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    try {
      socketService.disconnect();
      isConnectedRef.current = false;
      authenticatedRef.current = false;
    } catch (error) {
      console.error("Failed to disconnect from WebSocket:", error);
    }
  }, []);

  // Authenticate socket connection
  const authenticate = useCallback((authToken?: string, authUserType?: string) => {
    const tokenToUse = authToken || token;
    const userTypeToUse = authUserType || userType;

    if (tokenToUse && socketService.getConnectionStatus()) {
      socketService.authenticate(tokenToUse, userTypeToUse);
      authenticatedRef.current = true;
    }
  }, [token, userType]);

  // Join restaurant room
  const joinRestaurant = useCallback((restaurantIdToJoin?: string) => {
    const idToJoin = restaurantIdToJoin || restaurantId;
    
    if (idToJoin && socketService.getConnectionStatus() && authenticatedRef.current) {
      socketService.joinRestaurant(idToJoin);
    }
  }, [restaurantId]);

  // Update order status
  const updateOrderStatus = useCallback((
    orderId: string, 
    newStatus: string, 
    restaurantIdToUse?: string, 
    note?: string
  ) => {
    const idToUse = restaurantIdToUse || restaurantId;
    
    if (idToUse && socketService.getConnectionStatus()) {
      socketService.updateOrderStatus(orderId, newStatus, idToUse, note);
    }
  }, [restaurantId]);

  // Update table status
  const updateTableStatus = useCallback((
    tableId: string, 
    newStatus: string, 
    restaurantIdToUse?: string
  ) => {
    const idToUse = restaurantIdToUse || restaurantId;
    
    if (idToUse && socketService.getConnectionStatus()) {
      socketService.updateTableStatus(tableId, newStatus, idToUse);
    }
  }, [restaurantId]);

  // Update food item availability
  const updateFoodItemAvailability = useCallback((
    itemId: string, 
    isAvailable: boolean, 
    restaurantIdToUse?: string
  ) => {
    const idToUse = restaurantIdToUse || restaurantId;
    
    if (idToUse && socketService.getConnectionStatus()) {
      socketService.updateFoodItemAvailability(itemId, isAvailable, idToUse);
    }
  }, [restaurantId]);

  // Update dashboard
  const updateDashboard = useCallback((
    updateType: string, 
    data: any, 
    restaurantIdToUse?: string
  ) => {
    const idToUse = restaurantIdToUse || restaurantId;
    
    if (idToUse && socketService.getConnectionStatus()) {
      socketService.updateDashboard(idToUse, updateType, data);
    }
  }, [restaurantId]);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    return socketService.getConnectionStatus();
  }, []);

  // Setup event handlers
  useEffect(() => {
    const eventHandlers = {
      onOrderUpdated,
      onOrderReceived,
      onTableUpdated,
      onFoodItemUpdated,
      onDashboardUpdated,
      onAuthenticated,
      onAuthError,
      onError,
      onJoinedRestaurant,
    };

    // Remove any undefined handlers
    const cleanHandlers = Object.fromEntries(
      Object.entries(eventHandlers).filter(([_, handler]) => handler !== undefined)
    );

    socketService.setEventHandlers(cleanHandlers);

    return () => {
      socketService.removeEventHandlers();
    };
  }, [
    onOrderUpdated,
    onOrderReceived,
    onTableUpdated,
    onFoodItemUpdated,
    onDashboardUpdated,
    onAuthenticated,
    onAuthError,
    onError,
    onJoinedRestaurant,
  ]);

  // Auto-connect and authenticate when token is available
  useEffect(() => {
    if (token && !isConnectedRef.current) {
      connect();
    }
  }, [token, connect]);

  // Auto-authenticate when connected
  useEffect(() => {
    if (socketService.getConnectionStatus() && token && !authenticatedRef.current) {
      authenticate();
    }
  }, [token, authenticate]);

  // Auto-join restaurant when authenticated
  useEffect(() => {
    if (authenticatedRef.current && restaurantId) {
      joinRestaurant();
    }
  }, [restaurantId, joinRestaurant]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    authenticate,
    joinRestaurant,
    updateOrderStatus,
    updateTableStatus,
    updateFoodItemAvailability,
    updateDashboard,
    getConnectionStatus,
    isConnected: socketService.getConnectionStatus(),
  };
}; 